import { FC } from "react";
import styled, { useTheme } from "styled-components";
import { DateTime } from "luxon";

import { Td, Thumbnail } from "@/components/Table";
import Text from "@/components/Text";
import { ReleaseResource } from "@/types/resources";
import DistributionChannelBadge from "@/pages/DistributionVideoSummary/components/DistributionChannelBadge";
import DistributionTypeBadge from "./DistributionTypeBadge";
import thumbnailPlaceholder from "@/assets/icons/thumbnail-placeholder.svg";

const Link = styled(Text)`
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LinkWrapper = styled.div`
  display: flex;
  align-items: center;
  svg {
    flex-shrink: 0;
    margin-right: 4px;
  }
`;

const StyledDistributionTypeBadge = styled(DistributionTypeBadge)`
  margin-top: -5px;
`;

interface Props {
  release: ReleaseResource;
}

const DistributionScheduleDetailsTableRow: FC<Props> = ({ release }) => {
  const theme = useTheme();

  return (
    <tr>
      <Td>{release.projectId}</Td>
      <Td>
        <Thumbnail
          src={
            release.thumbnail?.versions?.preview ??
            release.thumbnail?.versions?.original ??
            thumbnailPlaceholder
          }
        />
      </Td>
      <Td>
        <Text $type="smallHeadline">{release.title}</Text>
      </Td>
      <Td>{release.freshId ?? "-"}</Td>
      <Td>
        {release.channel ? (
          <DistributionChannelBadge channel={release.channel.name} />
        ) : (
          "-"
        )}
      </Td>
      <Td>
        <StyledDistributionTypeBadge type={release.compilationType} />
      </Td>
      <Td>
        {release.releasedAt
          ? DateTime.fromISO(release.releasedAt).toLocaleString()
          : "-"}
      </Td>
      <Td>
        {release?.employees?.find((e) => e.release_role === "director")?.name ??
          "-"}
      </Td>
      <Td>
        {release?.employees?.find((e) => e.release_role === "editor")?.name ??
          "-"}
      </Td>
      <Td>
        {release?.employees?.find((e) => e.release_role === "publisher")
          ?.name ?? "-"}
      </Td>
      <Td>
        {release?.employees?.find((e) => e.release_role === "archivist")
          ?.name ?? "-"}
      </Td>
      <Td>
        {release.link ? (
          <LinkWrapper>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="14" height="14" fill={theme.colors.grayscaleLight} />
              <path
                d="M5.69434 8.30519L8.30519 5.69434"
                stroke={theme.colors.gray}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.6523 8.95851L6.56444 10.0464C6.2182 10.3925 5.74866 10.5869 5.25908 10.5869C4.76951 10.5868 4.29999 10.3923 3.95381 10.0461C3.60763 9.69996 3.41313 9.23045 3.41309 8.74087C3.41304 8.2513 3.60747 7.78175 3.95359 7.43551L5.04144 6.34766"
                stroke={theme.colors.gray}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.95851 7.65236L10.0464 6.5645C10.3925 6.21826 10.5869 5.74872 10.5869 5.25914C10.5868 4.76957 10.3923 4.30006 10.0461 3.95387C9.69996 3.60769 9.23045 3.41319 8.74088 3.41315C8.2513 3.41311 7.78176 3.60753 7.43551 3.95365L6.34766 5.04151"
                stroke={theme.colors.gray}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Link $type="smallNormal" as="a">
              {release.link}
            </Link>
          </LinkWrapper>
        ) : (
          "-"
        )}
      </Td>
      <Td>{release.part ?? "-"}</Td>
      <Td>{release.compilationGroup ?? "-"}</Td>
      <Td>{release.tips?.length ? "Yes" : "No"}</Td>
      <Td>{release.description ?? "-"}</Td>
      <Td>{release.notes || "-"}</Td>
    </tr>
  );
};

export default DistributionScheduleDetailsTableRow;
